import type { ImportMatchGradeValue, ImportStagedDecisionValue, ImportStagedKindValue, ImportStagedOutcomeValue } from './importStaging';

/**
 * 🔒 The import match log (piece 31; decision 5981; row T2746): one row per
 * staged row of a run, with the match the import proposed, the validation
 * study's suggestions where it ran, the person's decision, what an Edit
 * changed and what was written in the end. The run's page downloads it as a
 * CSV and a JSON of the same rows, and Copy for Claude puts it on the clipboard
 * under one line asking which match rules to adjust and why.
 *
 * 🔨 The opening line and the headings are the asking's own words, as the Deal
 * Pack's are, and are never passed through `t()`. The log carries no incoming
 * value. An Edit's row holds, per changed field of an account, a contact or an
 * item, the value it converted to and the value typed; every other converted
 * value stays out, a sealed field or a document's field is named without a value,
 * and nothing Confidential reaches it. 🔒 A Reject row's reason is carried as the
 * importer's sentence with every incoming value it quotes cut (decision 12911,
 * keeping 5981; T3866), through `matchLogReason` below.
 */

/*
 * 🔒 Decision 12911, keeping 5981: a reason sentence marks each incoming value it quotes where it is written, and the
 * match log and the Undecided list read it through `matchLogReason`, which cuts what is marked. The marks are the
 * Unicode isolates a quoted fragment of unknown direction wants anyway, which every screen draws as nothing, so the
 * detail pane shows the sentence whole: a value sits between FIRST STRONG ISOLATE and POP DIRECTIONAL ISOLATE, and a
 * service's thrown message, which cannot be told apart from a value, between LEFT-TO-RIGHT ISOLATE and the same close.
 */
const VALUE_OPEN = '\u2068';
const MESSAGE_OPEN = '\u2066';
const MARK_CLOSE = '\u2069';
/** Every isolate mark, the two above and RIGHT-TO-LEFT ISOLATE, which a value is cleaned of so it cannot forge a span. */
const ANY_MARK = /[\u2066-\u2069]/g;
const HAS_MARK = /[\u2066-\u2069]/;
const VALUE_SPAN = /\u2068[^\u2066-\u2069]*\u2069/g;
/** The slot a marked value stands at in a sentence's skeleton, which no reason carries. */
const SLOT = '\u0000';

/** An incoming value as a reason quotes it: marked, so the match log cuts it, and cleaned of any mark it carried. */
export function incomingInReason(value: string): string {
  return `${VALUE_OPEN}${value.replace(ANY_MARK, '')}${MARK_CLOSE}`;
}

/**
 * A service's thrown message as a reason quotes it: marked whole, so the match log carries a fixed sentence in its
 * place. A closing period stays outside the mark, so a writer that drops it still can.
 */
export function thrownInReason(message: string): string {
  const bare = message.replace(ANY_MARK, '');
  const period = bare.endsWith('.') ? '.' : '';
  return `${MESSAGE_OPEN}${period ? bare.slice(0, -1) : bare}${MARK_CLOSE}${period}`;
}

/** 🔒 The sentence the log carries for a reason that quotes a service's message: it names the step and holds nothing of the message. */
export const MATCH_LOG_THROWN_REASON = 'the read refused this row in the words of a service it called, which can quote what it was sent, so Step Through’s detail pane shows them for the row selected';
/** The words a marked value reads as in a sentence the log has no fixed form of. */
export const MATCH_LOG_VALUE_PLACEHOLDER = 'an incoming value';

/** A reason sentence that quotes incoming values: its words around each value, and the fixed sentence the log carries for it. */
export interface QuotingReason {
  readonly words: (...values: string[]) => string;
  readonly cut: string;
}

const CUTS = new Map<string, string>();
function quoting(words: (...values: string[]) => string, cut: string): QuotingReason {
  CUTS.set(words(...Array.from({ length: words.length }, () => SLOT)), cut);
  return { words, cut };
}

/**
 * 🔒 The reason sentences that quote an incoming value, each with the fixed sentence the log reads in its place, in the
 * sentence's own words (decision 12911). A writer passes the value through `reasonQuoting`, never into the words itself.
 */
export const QUOTING_REASONS = {
  /** A CSV row's Country cell that is not a two-letter code. */
  csvCountry: quoting((country) => `its Country ‘${country}’ is not a two-letter code such as US, so its match cannot be read`, 'the incoming country is not a two-letter code such as US, so its match cannot be read'),
  /** A Zoho account whose type no account-type row maps. */
  zohoAccountType: quoting((type) => `Zoho's account type ${type} has no place in the chart here; add the account by hand`, 'the incoming account type has no place in the chart here; add the account by hand'),
} as const;

/** A quoting reason written with its values, each marked. */
export function reasonQuoting(reason: QuotingReason, ...values: string[]): string {
  return reason.words(...values.map(incomingInReason));
}

/**
 * 🔒 A reason as the match log, its CSV, the copied text, its JSON and the Undecided list's hover carry it (decision
 * 12911, keeping 5981): a sentence that quotes a service's message reads the fixed sentence naming the step, a sentence
 * whose values are marked reads its fixed form, or each value as the placeholder where it has none, and a sentence with
 * no mark reads as written. Any mark left over reads the fixed sentence, so a stray mark never lets a value through.
 */
export function matchLogReason(reason: string | null): string | null {
  if (reason === null) return null;
  if (reason.includes(MESSAGE_OPEN)) return MATCH_LOG_THROWN_REASON;
  if (!HAS_MARK.test(reason)) return reason;
  const known = CUTS.get(reason.replace(VALUE_SPAN, SLOT));
  if (known !== undefined) return known;
  const cut = reason.replace(VALUE_SPAN, MATCH_LOG_VALUE_PLACEHOLDER);
  return HAS_MARK.test(cut) ? MATCH_LOG_THROWN_REASON : cut;
}

/** A reason with its marks taken out, as a person reads it whole. */
export function reasonShown(reason: string): string {
  return reason.replace(ANY_MARK, '');
}

/** The one line the copied log opens on. */
export const MATCH_LOG_OPENING_LINE = 'Tell me which of the import’s match rules to adjust, and why, from the match log below';

/** What the write ended in: the row's state once decided. 🔒 A written Associate reads Associated (decision 7940). */
export const MATCH_LOG_WROTE = ['Undecided', 'Skipped', 'Created', 'Updated', 'Associated', 'Unchanged', 'Refused'] as const;
export type MatchLogWrote = (typeof MATCH_LOG_WROTE)[number];

/** The route: the run's rows as JSON, which the page turns into the CSV, the JSON file and the copied text, so the three are the same rows. */
export const importRunMatchLogPath = (runId: string): string => `/import/runs/${encodeURIComponent(runId)}/match-log`;

/** One mark of the validation study as the log holds it; a sealed field's suggestion is never held. */
export interface MatchLogSuggestion {
  field: string;
  /** The contact person's Zoho id the mark names, empty on the company's own fields. */
  person: string | null;
  suggested: string;
  confidence: string | null;
  source: string | null;
}

/** One field an Edit changed: the value the row converted to and the value typed. */
export interface MatchLogEdit {
  field: string;
  from: string | null;
  to: string | null;
}

/** One row of the log, as the JSON file and the CSV carry it. */
export interface MatchLogRow {
  sequence: number;
  sourceKind: ImportStagedKindValue;
  sourceId: string;
  sourceLabel: string;
  proposedOutcome: ImportStagedOutcomeValue;
  matchedKind: string | null;
  matchedKey: string | null;
  matchedLabel: string | null;
  matchGrade: ImportMatchGradeValue;
  matchRuleKey: string | null;
  matchReason: string | null;
  matchSetAside: boolean;
  studyState: string | null;
  suggestions: MatchLogSuggestion[];
  decision: ImportStagedDecisionValue | null;
  doneForAll: boolean;
  editedFields: MatchLogEdit[];
  mergedKind: string | null;
  mergedKey: string | null;
  mergedLabel: string | null;
  wrote: MatchLogWrote;
  wroteKind: string | null;
  wroteKey: string | null;
  wroteLabel: string | null;
  refusal: string | null;
  decidedAt: string | null;
  /** 🔒 A Reject row's `proposedReason` through `matchLogReason`, every incoming value cut, and null on every other row (decision 12911; T3866). */
  reason: string | null;
}

/**
 * 🔒 The software the server runs (T3169): the deployed commit, its short id and the day this process came up under it,
 * which is the day it was deployed. Read once from the server's own environment, never from git at request time.
 */
export interface SoftwareRevision {
  commit: string;
  shortId: string;
  /** The calendar day, `YYYY-MM-DD`, the running process started. */
  deployedOn: string;
}

/** The commit a box without `BMS2_COMMIT` runs as; it names no deploy, so its day is the process's start. */
const LOCAL_COMMIT = 'local';

/** A revision as a person reads it: the short id and the day it was deployed, or the day the process started where the box carries no commit (T3203). */
export function softwareRevisionText(revision: SoftwareRevision): string {
  return `${revision.shortId}, ${revision.commit === LOCAL_COMMIT ? 'started' : 'deployed'} ${revision.deployedOn}`;
}

/** What the JSON route answers. */
export interface MatchLogAnswer {
  runId: string;
  /** The run in a line: its block and range, for the file's name and the copied text. */
  runLabel: string;
  /** The environment kind the server runs as: development, test or production (T3169). */
  environment: string;
  /** The software revision answering this read (T3169). */
  revision: SoftwareRevision;
  /** True where the run started before this revision was deployed, so it was started under an earlier one the run does not name (T3169). */
  startedBeforeRevision: boolean;
  /** When the server answered, the time of the export (T3169). */
  exportedAt: string;
  /**
   * 🔒 An items read's pictures (decision 8260): the rows whose Zoho item named a picture, and the pictures written here
   * for them. Absent on every other block's log.
   */
  pictures?: MatchLogPictures;
  rows: MatchLogRow[];
}

/** 🔒 The picture count an items read's export carries (decision 8260). */
export interface MatchLogPictures {
  read: number;
  written: number;
}

/** The decision as a person reads it: Do the same and Not This One are the person's own acts, kept beside Accept and Skip. */
export function matchLogDecisionText(row: Pick<MatchLogRow, 'decision' | 'doneForAll' | 'matchSetAside'>): string {
  const base = row.decision === null ? (row.matchSetAside ? 'Not This One' : 'Undecided') : row.decision;
  const parts = [base];
  if (row.decision !== null && row.matchSetAside) parts.push('after Not This One');
  if (row.doneForAll) parts.push('by Do the same for every undecided row');
  return parts.length === 1 ? base : `${base} (${parts.slice(1).join(', ')})`;
}

/** The CSV's columns, in order: each a heading and what the row gives under it. */
const COLUMNS: readonly { heading: string; of: (row: MatchLogRow) => string }[] = [
  { heading: 'Sequence', of: (row) => String(row.sequence) },
  { heading: 'Source Kind', of: (row) => row.sourceKind },
  { heading: 'Source Id', of: (row) => row.sourceId },
  { heading: 'Source Label', of: (row) => row.sourceLabel },
  { heading: 'Proposed Outcome', of: (row) => row.proposedOutcome },
  { heading: 'Matched Kind', of: (row) => row.matchedKind ?? '' },
  { heading: 'Matched Record', of: (row) => row.matchedLabel ?? '' },
  { heading: 'Match Strength', of: (row) => row.matchGrade },
  { heading: 'Match Rule', of: (row) => row.matchRuleKey ?? '' },
  { heading: 'Match Reason', of: (row) => row.matchReason ?? '' },
  { heading: 'Study', of: (row) => row.studyState ?? '' },
  { heading: 'Suggestions', of: (row) => row.suggestions.map((one) => `${one.field}${one.person ? ` of ${one.person}` : ''}: ${one.suggested}${one.confidence ? ` (${one.confidence}${one.source ? `, ${one.source}` : ''})` : ''}`).join('; ') },
  { heading: 'Decision', of: (row) => matchLogDecisionText(row) },
  { heading: 'Edited', of: (row) => row.editedFields.map((one) => `${one.field}: ${one.from ?? ''} → ${one.to ?? ''}`).join('; ') },
  { heading: 'Merged Into', of: (row) => row.mergedLabel ?? '' },
  { heading: 'Written', of: (row) => row.wrote },
  { heading: 'Written Record', of: (row) => row.wroteLabel ?? '' },
  { heading: 'Refusal', of: (row) => row.refusal ?? '' },
  { heading: 'Decided At', of: (row) => row.decidedAt ?? '' },
  // 🔒 Decision 12911: cut again here, so a row read from anywhere never carries a marked value into the file or the copy.
  { heading: 'Reason', of: (row) => matchLogReason(row.reason) ?? '' },
];

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * 🔒 The export's first line (T3169): the run, the environment, the software revision and the time of the export, so two
 * logs from two deploys tell apart. A run started before the revision was deployed says so, because it is then read
 * under a revision other than the one that started it.
 */
export function matchLogHeadLine(answer: Pick<MatchLogAnswer, 'runId' | 'runLabel' | 'environment' | 'revision' | 'startedBeforeRevision' | 'exportedAt' | 'pictures'>): string {
  const earlier = answer.startedBeforeRevision ? '; the run started before this revision was deployed, under an earlier one' : '';
  // 🔒 Decision 8260: an items read's line counts the pictures its rows read from Zoho Inventory against those written here.
  const pictures = answer.pictures ? `; pictures read ${answer.pictures.read}, written ${answer.pictures.written}` : '';
  return `Match log of run ${answer.runId.slice(0, 8)} (${answer.runLabel}); environment ${answer.environment}; software revision ${softwareRevisionText(answer.revision)}${earlier}${pictures}; exported ${answer.exportedAt}`;
}

/** The rows as a CSV, a first line naming the run and the software where one is given, then one line each under a heading line, every value quoted where it carries a comma, a quote or a line break. */
export function matchLogCsv(rows: readonly MatchLogRow[], headLine: string | null = null): string {
  return [...(headLine === null ? [] : [csvCell(headLine)]), COLUMNS.map((column) => csvCell(column.heading)).join(','), ...rows.map((row) => COLUMNS.map((column) => csvCell(column.of(row))).join(','))].join('\r\n') + '\r\n';
}

/**
 * What Copy for Claude puts on the clipboard: the asking line, the run in a
 * line, and the rows as the CSV carries them, so the copy and the download
 * are the same rows.
 */
export function matchLogClaudeText(runLabel: string, rows: readonly MatchLogRow[], headLine: string | null = null): string {
  return [MATCH_LOG_OPENING_LINE, '', '# Import Run', runLabel, ...(headLine === null ? [] : [headLine]), '', '# Match Log', '```csv', matchLogCsv(rows).trimEnd(), '```', ''].join('\n');
}
